import {
  camelCase,
  snakeCase as lodashSnakeCase,
  isDate,
  isEmpty,
  isPlainObject,
  isString,
  mapKeys,
  mapValues,
} from 'lodash-es'
export { camelCase, isPlainObject, mapKeys, mapValues }
export { lodashSnakeCase as snakeCase }
export { klona as cloneDeep } from 'klona/json'

// Internal: Converts a string to snake_case, preserving Rails nested-attribute meta-keys.
// lodash's snakeCase strips leading underscores (_destroy → destroy, _id → id),
// but Rails expects these keys (e.g. _destroy, _id) to retain the leading underscore.
function railsSnakeCase (str: string): string {
  // Preserve Rails-style meta-keys that start with "_" and are already in snake_case format
  if (str.startsWith('_') && /^_[a-z0-9_]+$/.test(str)) return str
  return lodashSnakeCase(str)
}

// Public: Consistent with the Rails definition.
export function isBlank (val: any): val is undefined | null | '' | false {
  if (val === true || (typeof val === 'number' && !isNaN(val)) || isDate(val)) return false
  if (isPhone(val) && isEmpty(val.number)) return true
  return isEmpty(isString(val) ? val.trim() : val)
}

function isPhone (val: any) {
  return typeof val === 'object' && val !== null && 'countryDialingCode' in val
}

// Public: Consistent with the Rails definition.
export function isPresent (val: any) {
  return !isBlank(val)
}

// Public: Consistent with the Rails definition.
export function presence<T> (object: T): T | undefined {
  return isPresent(object) ? object : undefined
}

// Public: Removes any empty values from an object.
export function clean<T extends object> (object: T): Partial<T> {
  for (const key in object) {
    const value = object[key] as any
    if (isBlank(value) && value !== false)
      delete object[key]
  }

  return object
}

// Public: Removes any empty values from an object, recursively.
export function deepClean<T extends object> (object: T): Partial<T> {
  if (Array.isArray(object))
    return object.map((obj: any) => deepClean(obj) as any) as any

  if (isPlainObject(object)) {
    for (const key in object) {
      const value = deepClean(object[key] as any) as any
      if (isBlank(value))
        delete object[key]
    }
  }

  return object
}

// NOTE: For nested errors, keys like `contact.phone` should not be camel cased
// to `contactPhone`.
export function safeCamelCase (field: string) {
  return String(field).split('.').map(camelCase).join('.')
}

function convertKeys (object: any, keyConverter = safeCamelCase) {
  if (isPlainObject(object))
    return mapKeys(object, (value, key) => keyConverter(key))
  else
    return object
}

function deepConvertKeys (object: any, keyConverter = decamelizeKeys, decamelizer = railsSnakeCase): any {
  if (isPlainObject(object)) {
    return mapValues(
      keyConverter(object, decamelizer),
      (value: any) => deepConvertKeys(value, keyConverter, decamelizer),
    )
  }

  if (Array.isArray(object))
    return object.map((item: any) => deepConvertKeys(item, keyConverter, decamelizer))

  return object
}

// Public: Converts all object keys to camelCase, preserving the values.
export function camelizeKeys (object: any) {
  return convertKeys(object, safeCamelCase)
}

// Public: Converts all object keys to snake_case, preserving the values.
export function decamelizeKeys (object: any, decamelizer = railsSnakeCase) {
  return convertKeys(object, decamelizer)
}

// Public: Converts all object keys to camelCase, as well as nested objects, or
// objects in nested arrays.
export function deepCamelizeKeys (object: any) {
  return deepConvertKeys(object, camelizeKeys)
}

// Public: Converts all object keys to snake_case, as well as nested objects, or
// objects in nested arrays.
export function deepDecamelizeKeys (object: any, decamelizer = railsSnakeCase) {
  return deepConvertKeys(object, decamelizeKeys, decamelizer)
}
